import React from 'react';
import {
  Input,
  Label,
  makeStyles,
  Radio,
  RadioGroup,
  tokens,
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { DayOfWeek, FirstWeekOfYear } from '@fluentui/react-calendar-compat';
import { useMsal } from '@azure/msal-react';
import type { IStammdatenItem } from '../../models/IStammdatenItem';
import { useLocation } from 'react-router-dom';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
  section: {
    padding: '24px',
    backgroundColor: tokens.colorNeutralBackground2, //'#f9f9f9',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  fieldLabel: {
    fontWeight: '600',
    fontSize: '16px',
    color: tokens.colorNeutralForeground2LinkSelected, //'#444',
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '5px',
  },
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const onFormatDate = (d?: Date): string => {
  const year = new Intl.DateTimeFormat('de', { year: 'numeric' }).format(d);
  const month = new Intl.DateTimeFormat('de', { month: '2-digit' }).format(d);
  const day = new Intl.DateTimeFormat('de', { day: '2-digit' }).format(d);
  return `${day}.${month}.${year}`;
};

const Personaldata: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);

  const [detailData, setDetailData] = React.useState<IStammdatenItem | null>(
    null
  );

  React.useEffect(() => {
    const fetchItems = async () => {
      if (!dataService) return;
      if (!location.state?.personalGuid) return;
      const response = await dataService.getPersonalStammdaten(
        location.state?.personalGuid
      );
      if (response) setDetailData(response);
    };
    fetchItems();
  }, [dataService, location.state.personalGuid]);

  return detailData ? (
    <div className="verticalLayout">
      <div className={styles.section}>
        <h2 className="sectionTitle">Persönliche Daten</h2>
        <div className={styles.gridContainer}>
          <div className={styles.fieldGroup}>
            <Label
              htmlFor="vorname"
              className={styles.fieldLabel}
              style={{ paddingTop: '0px' }}
            >
              Vorname
            </Label>
            <Input
              className="fieldInput"
              id="vorname"
              disabled
              value={detailData?.vorname || ''}
              autoFocus
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label
              htmlFor="nachname"
              className={styles.fieldLabel}
              style={{ paddingTop: '0px' }}
            >
              Nachname
            </Label>
            <Input
              className="fieldInput"
              id="nachname"
              disabled
              value={detailData?.nachname || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="titel" className={styles.fieldLabel}>
              Titel
            </Label>
            <Input
              className="fieldInput"
              id="titel"
              disabled
              value={detailData?.titel || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="titelNach" className={styles.fieldLabel}>
              Titel Nach
            </Label>
            <Input
              className="fieldInput"
              id="titelNach"
              disabled
              value={detailData?.titelNach || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="verliehenerTitel" className={styles.fieldLabel}>
              Verliehener Titel
            </Label>
            <Input
              className="fieldInput"
              id="verliehenerTitel"
              disabled
              value={detailData?.verliehenerTitel || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="fhTitel" className={styles.fieldLabel}>
              FH-Titel
            </Label>
            <Input
              className="fieldInput"
              id="fhTitel"
              disabled
              value={detailData?.fhTitel || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="geburtsdatum" className={styles.fieldLabel}>
              Geburtsdatum
            </Label>
            <DatePicker
              className="fieldInput"
              id="geburtsdatum"
              disabled
              allowTextInput={true}
              firstDayOfWeek={DayOfWeek.Monday}
              showWeekNumbers={true}
              showGoToToday={true}
              firstWeekOfYear={FirstWeekOfYear.FirstFourDayWeek}
              formatDate={onFormatDate}
              value={
                detailData?.geburtsdatum
                  ? new Date(detailData.geburtsdatum)
                  : null
              }
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="svnr" className={styles.fieldLabel}>
              SV-Nummer
            </Label>
            <Input
              className="fieldInput"
              id="svnr"
              disabled
              value={detailData?.svnr || ''}
            />
          </div>
          <div className={styles.fieldGroup}>
            <Label htmlFor="geschlecht" className={styles.fieldLabel}>
              Geschlecht
            </Label>
            <RadioGroup layout="horizontal">
              <Radio
                value="m"
                label="Männlich"
                checked={detailData?.geschlecht === 'm'}
              />
              <Radio
                value="w"
                label="Weiblich"
                checked={detailData?.geschlecht === 'w'}
              />
              <Radio
                value="x"
                label="Divers"
                checked={detailData?.geschlecht === 'x'}
              />
            </RadioGroup>
          </div>
        </div>
      </div>
    </div>
  ) : (
    <p
      style={{
        fontSize: '16px',
        marginBottom: '30px',
        maxWidth: '500px',
      }}
    >
      Keine Person ausgewählt.
    </p>
  );
};

export default Personaldata;
